import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen } from "lucide-react";
import type { Workspace } from "@/hooks/useWorkspace";
import type { ReviewItem } from "./useDowngradeReviewData";

const labelFor = (w: { workspace_label: string | null; display_name: string | null }) =>
  w.workspace_label || w.display_name || "Workspace";

type Props = {
  loading: boolean;
  items: ReviewItem[];
  workspaces: Workspace[];
  survivingWorkspaces: Workspace[];
  selected: Set<string>;
  bulkTarget: string;
  busy: boolean;
  cutoverDate: string;
  onToggleOne: (id: string) => void;
  onToggleAll: () => void;
  onBulkTargetChange: (v: string) => void;
  onBulkMove: () => void;
  onBulkDelete: () => void;
  onMoveOne: (item: ReviewItem, target: string) => void;
  onDeleteOne: (item: ReviewItem) => void;
};

export const AtRiskItemsCard = ({
  loading,
  items,
  workspaces,
  survivingWorkspaces,
  selected,
  bulkTarget,
  busy,
  cutoverDate,
  onToggleOne,
  onToggleAll,
  onBulkTargetChange,
  onBulkMove,
  onBulkDelete,
  onMoveOne,
  onDeleteOne,
}: Props) => {
  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="text-base">At-risk library items ({items.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 space-y-2">
            <BookOpen className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Nothing to review — every library item in the affected workspaces is also visible in a surviving workspace.
            </p>
          </div>
        ) : (
          <>
            {survivingWorkspaces.length === 0 ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
                No surviving workspaces to move items to. You can only choose to keep (archive) or delete now.
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2 border-y border-border py-2">
              <button
                type="button"
                onClick={onToggleAll}
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Checkbox checked={allSelected} />
                <span>{selected.size} selected</span>
              </button>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {survivingWorkspaces.length > 0 ? (
                  <>
                    <Select value={bulkTarget} onValueChange={onBulkTargetChange}>
                      <SelectTrigger className="h-9 w-[200px]">
                        <SelectValue placeholder="Move selected to…" />
                      </SelectTrigger>
                      <SelectContent>
                        {survivingWorkspaces.map((w) => (
                          <SelectItem key={w.id} value={w.id}>
                            {labelFor(w)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onBulkMove}
                      disabled={busy || selected.size === 0 || !bulkTarget}
                    >
                      Move
                    </Button>
                  </>
                ) : null}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={onBulkDelete}
                  disabled={busy || selected.size === 0}
                >
                  Delete now
                </Button>
              </div>
            </div>

            <ul className="divide-y divide-border rounded-md border border-border">
              {items.map((it) => {
                const owning = workspaces.find((w) => w.id === it.workspace_id);
                return (
                  <li key={it.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <Checkbox
                        checked={selected.has(it.id)}
                        onCheckedChange={() => onToggleOne(it.id)}
                        className="mt-1"
                      />
                      <div className="min-w-0 space-y-1">
                        <p className="truncate text-sm font-medium text-foreground">
                          {it.display_title || it.title}
                          {it.subtitle ? <span className="text-muted-foreground">: {it.subtitle}</span> : null}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {it.author_text ? `${it.author_text} · ` : ""}
                          in <span className="text-foreground">{owning ? labelFor(owning) : "—"}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {survivingWorkspaces.length > 0 ? (
                        <Select onValueChange={(v) => onMoveOne(it, v)} disabled={busy}>
                          <SelectTrigger className="h-9 w-[180px]">
                            <SelectValue placeholder="Move to…" />
                          </SelectTrigger>
                          <SelectContent>
                            {survivingWorkspaces.map((w) => (
                              <SelectItem key={w.id} value={w.id}>
                                {labelFor(w)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : null}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDeleteOne(it)}
                        disabled={busy}
                      >
                        Delete
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <p className="text-xs text-muted-foreground">
              Items left untouched will be archived along with their workspace on {cutoverDate}. Use the per-row "Move to…" or the bulk control to keep items in a surviving workspace.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};
